/**
 * useTestDataGeneration Hook
 * Handles test data generation flow with SSE streaming progress
 *
 * @see TECHNICAL_DEBT_ROADMAP.md Phase 3.1
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { TestDataProgress, TestDataPhase } from '~/types/test-data';
import { getAuthToken } from './useTestDataStatus';

/**
 * SSE event data structure from backend
 */
interface SSEProgressData {
  phase: TestDataPhase;
  progress: number;
  total: number;
  percentage: number;
  message: string;
  result?: {
    variantsCreated: number;
    velocityRecordsCreated: number;
    inventoryRecordsCreated: number;
    alertsGenerated: number;
    seasonalPatternsCreated: number;
  };
  error?: string;
}

/**
 * Parse SSE events from a text buffer
 * SSE format: "event: eventName\ndata: jsonData\n\n"
 */
function parseSSEBuffer(buffer: string): {
  events: Array<{ event: string; data: SSEProgressData }>;
  remaining: string;
} {
  const events: Array<{ event: string; data: SSEProgressData }> = [];
  const parts = buffer.split('\n\n');
  const remaining = parts.pop() || '';

  for (const part of parts) {
    if (!part.trim()) continue;

    const lines = part.split('\n');
    let eventType = 'message';
    let data = '';

    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventType = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        data = line.slice(5).trim();
      }
    }

    if (data) {
      try {
        events.push({ event: eventType, data: JSON.parse(data) });
      } catch {
        // Failed to parse SSE data - skip
      }
    }
  }

  return { events, remaining };
}

interface UseTestDataGenerationOptions {
  /** Whether App Bridge is ready */
  isReady: boolean;
  /** Callback when generation completes */
  onComplete?: () => void;
  /** Callback when an error occurs */
  onError?: (error: string) => void;
  /** Refresh status after completion */
  fetchStatus: () => Promise<unknown>;
}

interface UseTestDataGenerationReturn {
  /** Whether generation is in progress */
  isGenerating: boolean;
  /** Current progress state */
  progress: TestDataProgress | null;
  /** Whether progress modal is open */
  progressModalOpen: boolean;
  /** Start generation process */
  startGeneration: () => Promise<void>;
  /** Close progress modal */
  closeProgressModal: () => void;
  /** Convenience handler for populate button */
  handlePopulateTestData: () => void;
}

/**
 * Hook for managing test data generation with SSE progress streaming
 */
export function useTestDataGeneration({
  isReady,
  onComplete,
  onError,
  fetchStatus,
}: UseTestDataGenerationOptions): UseTestDataGenerationReturn {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState<TestDataProgress | null>(null);
  const [progressModalOpen, setProgressModalOpen] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup SSE connection on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  // Start test data generation using fetch-based SSE stream
  const startGeneration = useCallback(async () => {
    if (!isReady) {
      onError?.('App Bridge not ready. Please wait and try again.');
      return;
    }

    // Abort any existing connection
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setIsGenerating(true);
    setProgress(null);
    setProgressModalOpen(true);

    // Initial progress state
    setProgress({
      jobId: 'sse-stream',
      shopId: 0,
      status: 'pending',
      phase: 'initializing',
      progress: 0,
      total: 100,
      message: 'Connecting to server...',
      percentage: 0,
      startedAt: new Date().toISOString(),
    });

    try {
      const token = await getAuthToken();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const headers: HeadersInit = {
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch('/api/test-data/stream', {
        method: 'GET',
        headers,
        signal: abortController.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      if (!response.body) {
        throw new Error('No response body for SSE stream');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let isComplete = false;

      while (!isComplete) {
        const { done, value } = await reader.read();

        if (done) {
          isComplete = true;
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const { events, remaining } = parseSSEBuffer(buffer);
        buffer = remaining;

        for (const { event, data } of events) {
          switch (event) {
            case 'progress':
              setProgress((prev) => ({
                ...prev!,
                status: 'in_progress',
                phase: data.phase,
                progress: data.progress,
                total: data.total,
                percentage: data.percentage,
                message: data.message,
              }));
              break;

            case 'complete':
              setProgress((prev) => ({
                ...prev!,
                status: 'completed',
                phase: data.phase,
                progress: data.progress,
                total: data.total,
                percentage: 100,
                message: data.message,
                result: data.result,
              }));

              isComplete = true;
              abortControllerRef.current = null;
              setIsGenerating(false);

              onComplete?.();
              await fetchStatus();
              break;

            case 'error':
              const errorMessage = data.error || data.message || 'Unknown error';

              setProgress((prev) => ({
                ...prev!,
                status: 'failed',
                phase: data.phase || 'initializing',
                progress: 0,
                total: 100,
                percentage: 0,
                message: data.message || 'Generation failed',
                error: errorMessage,
              }));

              isComplete = true;
              abortControllerRef.current = null;
              setIsGenerating(false);

              onError?.(errorMessage);
              break;
          }
        }
      }

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }

      abortControllerRef.current = null;
      setIsGenerating(false);

      const errorMessage = error instanceof Error ? error.message : 'Failed to connect to server';

      setProgress((prev) => ({
        ...prev!,
        status: 'failed',
        phase: 'initializing',
        message: 'Connection error',
        error: errorMessage,
      }));

      onError?.(errorMessage);
    }
  }, [isReady, onComplete, onError, fetchStatus]);

  // Close progress modal and cleanup connection
  const closeProgressModal = useCallback(() => {
    setProgressModalOpen(false);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setTimeout(() => {
      setProgress(null);
    }, 300);
  }, []);

  const handlePopulateTestData = useCallback(() => {
    startGeneration();
  }, [startGeneration]);

  return {
    isGenerating,
    progress,
    progressModalOpen,
    startGeneration,
    closeProgressModal,
    handlePopulateTestData,
  };
}
